/**
 * Shell Loading Context
 *
 * Provides a unified loading state for shell transitions (client switch, shell switch).
 * This prevents "jank" by:
 * - Showing a consistent loading indicator during transitions
 * - Preventing stale data from flashing during client switches
 * - Providing predictable loading behavior across the app
 *
 * Idempotency guarantees:
 * - Each transition has a unique ID
 * - endTransition only ends the current active transition
 * - Duplicate start calls are ignored if already transitioning to same target
 * - Prevents double-firing of analytics events
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

// =============================================================================
// TYPES
// =============================================================================

export type TransitionType =
  | "client-switch"
  | "shell-switch"
  | "initial-load"
  | null;

interface ShellLoadingState {
  /** Whether a shell transition is in progress */
  isTransitioning: boolean;
  /** The type of transition occurring */
  transitionType: TransitionType;
  /** The previous client ID (for stale data prevention) */
  previousClientId: string | null;
  /** The target client ID being switched to */
  targetClientId: string | null;
  /** Unique ID for the current transition (for idempotency) */
  transitionId: string | null;
  /** Timestamp when transition started (for duration tracking) */
  transitionStartTime: number | null;
}

interface ShellLoadingContextValue extends ShellLoadingState {
  /** Start a client switch transition. Returns transition ID or null if ignored. */
  startClientSwitch: (
    fromClientId: string | null,
    toClientId: string,
  ) => string | null;
  /** Start a shell switch transition. Returns transition ID or null if ignored. */
  startShellSwitch: () => string | null;
  /** End the current transition. Returns true if ended, false if no active transition. */
  endTransition: () => boolean;
  /** Check if we should show stale data for a client ID */
  isStaleClient: (clientId: string | null) => boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

let transitionCounter = 0;

function generateTransitionId(): string {
  transitionCounter += 1;
  return `transition-${transitionCounter}-${Date.now()}`;
}

// =============================================================================
// CONTEXT
// =============================================================================

const ShellLoadingContext = createContext<ShellLoadingContextValue | undefined>(
  undefined,
);

// =============================================================================
// PROVIDER
// =============================================================================

interface ShellLoadingProviderProps {
  children: ReactNode;
}

const INITIAL_STATE: ShellLoadingState = {
  isTransitioning: false,
  transitionType: null,
  previousClientId: null,
  targetClientId: null,
  transitionId: null,
  transitionStartTime: null,
};

export function ShellLoadingProvider({ children }: ShellLoadingProviderProps) {
  const [state, setState] = useState<ShellLoadingState>(INITIAL_STATE);

  // Track if we've already logged the end event for this transition
  const endedTransitionsRef = useRef<Set<string>>(new Set());

  const startClientSwitch = useCallback(
    (fromClientId: string | null, toClientId: string): string | null => {
      // Ignore if already transitioning to the same target
      if (state.isTransitioning && state.targetClientId === toClientId) {
        return null;
      }

      const transitionId = generateTransitionId();
      setState({
        isTransitioning: true,
        transitionType: "client-switch",
        previousClientId: fromClientId,
        targetClientId: toClientId,
        transitionId,
        transitionStartTime: Date.now(),
      });

      return transitionId;
    },
    [state.isTransitioning, state.targetClientId],
  );

  const startShellSwitch = useCallback((): string | null => {
    // Ignore if already in a shell switch
    if (state.isTransitioning && state.transitionType === "shell-switch") {
      return null;
    }

    const transitionId = generateTransitionId();
    setState((prev) => ({
      isTransitioning: true,
      transitionType: "shell-switch",
      previousClientId: prev.previousClientId,
      targetClientId: null,
      transitionId,
      transitionStartTime: Date.now(),
    }));

    return transitionId;
  }, [state.isTransitioning, state.transitionType]);

  const endTransition = useCallback((): boolean => {
    // Nothing to end
    if (!state.isTransitioning || !state.transitionId) {
      return false;
    }

    // Already ended this transition (prevents double-fire)
    if (endedTransitionsRef.current.has(state.transitionId)) {
      return false;
    }

    // Mark as ended
    endedTransitionsRef.current.add(state.transitionId);

    // Clean up old entries (keep last 50)
    if (endedTransitionsRef.current.size > 50) {
      const entries = Array.from(endedTransitionsRef.current);
      endedTransitionsRef.current = new Set(entries.slice(-50));
    }

    setState(INITIAL_STATE);
    return true;
  }, [state.isTransitioning, state.transitionId]);

  const isStaleClient = useCallback(
    (clientId: string | null): boolean => {
      if (!state.isTransitioning) return false;
      if (state.transitionType !== "client-switch") return false;
      // If we're switching clients and this is the previous client, it's stale
      return (
        clientId === state.previousClientId && clientId !== state.targetClientId
      );
    },
    [state],
  );

  const value = useMemo(
    () => ({
      ...state,
      startClientSwitch,
      startShellSwitch,
      endTransition,
      isStaleClient,
    }),
    [state, startClientSwitch, startShellSwitch, endTransition, isStaleClient],
  );

  return (
    <ShellLoadingContext.Provider value={value}>
      {children}
    </ShellLoadingContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access shell loading state.
 *
 * Usage:
 * ```tsx
 * const { isTransitioning, startClientSwitch, endTransition } = useShellLoading();
 *
 * // Start a transition
 * const id = startClientSwitch(currentClientId, newClientId);
 * if (id) {
 *   // New transition started
 * }
 *
 * // End transition when data is ready
 * useEffect(() => {
 *   if (dataLoaded) {
 *     const ended = endTransition();
 *     if (ended) {
 *       // Transition completed
 *     }
 *   }
 * }, [dataLoaded]);
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useShellLoading(): ShellLoadingContextValue {
  const context = useContext(ShellLoadingContext);
  if (!context) {
    throw new Error(
      "useShellLoading must be used within a ShellLoadingProvider",
    );
  }
  return context;
}

/**
 * Hook that returns just the transitioning state.
 * Useful for simple loading checks.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsTransitioning(): boolean {
  const { isTransitioning } = useShellLoading();
  return isTransitioning;
}

/**
 * Hook to get transition duration (useful for analytics).
 * Returns null if no transition in progress.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTransitionDuration(): number | null {
  const { isTransitioning, transitionStartTime } = useShellLoading();
  if (!isTransitioning || !transitionStartTime) return null;
  return Date.now() - transitionStartTime;
}
